import axios, { AxiosError, AxiosRequestConfig } from "axios";
import { RoutePaths } from "enums/RoutePaths";
import { useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../constants/apiBase";
import { getAppCheckToken, getIdToken, hasFirebaseUser } from "../firebase";

export interface ApiError {
  code: number;
  text: any;
  /** The server's own message ({ message } or { detail }), when it sent one. */
  message?: string;
}

export interface ApiResult<T> {
  data: T | null;
  isLoading: boolean;
  error: ApiError | null;
  execute: (
    url: string,
    method: RequestMethod,
    payload?: any,
    query?: any,
    customHeaders?: Record<string, string>,
  ) => Promise<T | null>;
  /** The error from the latest call, available right after `execute` resolves (unlike `error`). */
  lastError: () => ApiError | null;
}

type RequestMethod = "GET" | "POST" | "PATCH" | "DELETE" | "PUT";

const API_KEY = process.env.REACT_APP_API_KEY;

function useFetch<T>(): ApiResult<T> {
  const [data, setData] = useState<T | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const lastErrorRef = useRef<ApiError | null>(null);

  const location = useLocation();
  const navigate = useNavigate();
  const currentRoute = location.pathname;

  const execute = async (
    url: string,
    method: RequestMethod,
    payload?: any,
    params?: any,
    customHeaders?: Record<string, string>,
  ): Promise<T | null> => {
    setIsLoading(true);
    lastErrorRef.current = null;

    const appCheckToken = await getAppCheckToken();

    const buildOptions = async (
      forceRefresh: boolean,
    ): Promise<AxiosRequestConfig> => {
      const token = await getIdToken(forceRefresh);
      return {
        url: `${API_BASE_URL}${url}`,
        method,
        headers: {
          accept: "*/*",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...(appCheckToken ? { "X-Firebase-AppCheck": appCheckToken } : {}),
          ...customHeaders,
          ...(currentRoute !== RoutePaths.Login
            ? { "Api-key": API_KEY || "" }
            : {}),
        },
        data: payload,
        params: params,
      };
    };

    try {
      let response;
      try {
        response = await axios(await buildOptions(false));
      } catch (firstError: unknown) {
        // Expired token: get a new one from Firebase and try once more.
        if (
          firstError instanceof AxiosError &&
          firstError.response?.status === 401 &&
          hasFirebaseUser()
        ) {
          response = await axios(await buildOptions(true));
        } else {
          throw firstError;
        }
      }
      setData(response.data);
      setError(null);
      setIsLoading(false);
      return response.data;
    } catch (error: unknown) {
      if (
        error instanceof AxiosError &&
        error.response?.status === 401 &&
        !hasFirebaseUser() &&
        currentRoute !== RoutePaths.Login
      ) {
        // The Firebase session is gone, so the token can't be renewed.
        sessionStorage.clear();
        navigate(RoutePaths.Login);
      }
      let apiError: ApiError;
      if (error instanceof AxiosError && error.response) {
        const body = error.response.data;
        apiError = {
          code: error.response.status,
          text: body?.errors,
          message:
            typeof body?.message === "string"
              ? body.message
              : typeof body?.detail === "string"
                ? body.detail
                : undefined,
        };
      } else {
        apiError = { code: 520, text: "An error occurred." };
      }
      lastErrorRef.current = apiError;
      setError(apiError);
      setData(null);
      setIsLoading(false);
    }

    return null;
  };

  return {
    data,
    isLoading,
    error,
    execute,
    lastError: () => lastErrorRef.current,
  };
}

export default useFetch;
